import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    this.addEventListener('click', this.#handleBundleAdd);
    this.#syncBundleButtons();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantitySelectorUpdate);
    this.removeEventListener('click', this.#handleBundleAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    requestAnimationFrame(() => this.#syncBundleButtons());
  };

  /**
   * Keeps pack "Added" labels in sync when quantity is changed with +/-.
   * @param {CustomEvent} event
   */
  #handleQuantitySelectorUpdate = (event) => {
    if (!this.contains(event.target)) return;

    const quantity = Number(event.detail?.quantity);
    if (Number.isFinite(quantity) && quantity > 0) {
      this.#syncBundleButtons(quantity);
      return;
    }

    this.#syncBundleButtons();
  };

  /**
   * Marks the pack that matches the current cart quantity as Added.
   * @param {number} [quantity]
   */
  #syncBundleButtons = (quantity) => {
    const bundle = this.querySelector('.cart-bundle');
    if (!(bundle instanceof HTMLElement)) return;

    const currentQty = Number.isFinite(quantity) ? quantity : this.#readCartQty(bundle);
    const addLabel = bundle.dataset.addLabel || 'Add';
    const addedLabel = bundle.dataset.addedLabel || 'Added';

    bundle.dataset.currentQty = String(currentQty);

    bundle.querySelectorAll('.cart-bundle__add').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      const packQty = Number(button.dataset.cartQuantity);
      const isCurrent = packQty <= currentQty;
      const soldOut = button.dataset.soldOut === 'true';
      const item = button.closest('.cart-bundle__item');

      item?.classList.toggle('cart-bundle__item--current', isCurrent);
      button.disabled = isCurrent || soldOut;

      if (soldOut && !isCurrent) {
        return;
      }

      button.textContent = isCurrent ? addedLabel : addLabel;
    });
  };

  /**
   * @param {HTMLElement} bundle
   * @returns {number}
   */
  #readCartQty(bundle) {
    const line = bundle.querySelector('[data-cart-line]')?.dataset.cartLine;
    const input = line
      ? this.querySelector(`.cart-items .quantity-selector input[data-cart-line="${line}"]`)
      : this.querySelector('.cart-items .quantity-selector input');

    if (input instanceof HTMLInputElement) {
      return parseInt(input.value, 10) || 0;
    }

    return Number(bundle.dataset.currentQty) || 0;
  }

  /**
   * Sets the cart line quantity to a pack size (1, 2, or 3).
   * @param {MouseEvent} event
   */
  #handleBundleAdd = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-cart-add-variant]');
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const variantId = Number(button.dataset.cartAddVariant);
    const line = Number(button.dataset.cartLine);
    const quantity = Number(button.dataset.cartQuantity);
    if (!variantId || !line || !quantity) return;

    const cartItemsComponent = this.querySelector('cart-items-component');
    const sectionId =
      cartItemsComponent instanceof HTMLElement ? cartItemsComponent.dataset.sectionId : undefined;

    this.#syncBundleButtons(quantity);
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
        body: JSON.stringify({
          line,
          quantity,
          sections: sectionId,
          sections_url: window.location.pathname,
        }),
      }));
      const data = await response.json();

      if (data.status || data.errors) {
        this.#syncBundleButtons();
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, String(variantId), {
          source: 'cart-drawer-bundle',
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.#syncBundleButtons();
    } finally {
      button.removeAttribute('aria-busy');
      this.#syncBundleButtons(quantity);
    }
  };

  open() {
    this.showDialog();
    this.#syncBundleButtons();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
